import React, { useState, useEffect, useCallback } from 'react';
import { Receipt, FileText, Mail, CheckCircle2, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { buildInvoicePdf } from './invoiceUtils';
import InvoiceEmailModal from './InvoiceEmailModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const eur = (n) => `€${Number(n || 0).toFixed(2)}`;
const fmtDT = (iso, pt) => iso ? new Date(iso).toLocaleDateString(pt ? 'pt-PT' : 'en-IE', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

const ClientInvoicesList = ({ phone, token, language, refreshKey }) => {
  const pt = language === 'pt';
  const headers = { Authorization: `Bearer ${token}` };
  const [data, setData] = useState(null);
  const [emailInv, setEmailInv] = useState(null);
  const [toast, setToast] = useState('');

  const load = useCallback(async () => {
    if (!phone) { setData({ invoices: [], total_due: 0 }); return; }
    const { data: d } = await axios.get(`${API}/admin/invoices`, { params: { phone }, headers });
    setData(d);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phone, token, refreshKey]);
  useEffect(() => { load().catch(() => setData({ invoices: [], total_due: 0 })); }, [load]);

  const full = async (inv) => (await axios.get(`${API}/admin/invoices/${inv.id}`, { headers })).data;
  const pdf = async (inv) => {
    const f = await full(inv);
    buildInvoicePdf({ number: f.number, client: { name: f.client_name, phone: f.client_phone, email: f.client_email, address: f.client_address, eircode: f.client_eircode },
      items: f.items, total: f.live_total, paid: f.live_paid, due: f.live_due, settings: f.settings || {}, from: f.date_from, to: f.date_to, notes: f.notes, signedBy: f.signed_by, pt })
      .save(`${f.number}_${(f.client_name || 'cliente').replace(/\s+/g, '_')}.pdf`);
  };

  if (!data) return null;
  const due = data.invoices.filter(i => i.live_due > 0);
  const paid = data.invoices.filter(i => i.live_due <= 0);

  return (
    <div data-testid="client-invoices">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2"><Receipt size={16} className="text-[#1a5c3a]" /> {pt ? 'Faturas emitidas' : 'Issued invoices'} <span className="text-xs text-gray-400 font-normal">({data.invoices.length})</span></h3>
        <span className={`text-sm font-bold px-3 py-1 rounded-full ${data.total_due > 0 ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`} data-testid="client-invoices-due">
          {data.total_due > 0 ? `${pt ? 'Em falta' : 'Outstanding'}: ${eur(data.total_due)}` : (pt ? 'Sem valores em falta' : 'Nothing outstanding')}
        </span>
      </div>
      {toast && <p className="text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg p-2 mb-2" data-testid="client-invoices-toast">{toast}</p>}
      {data.invoices.length === 0 ? (
        <p className="text-sm text-gray-500 bg-gray-50 border border-dashed rounded-xl p-3 text-center" data-testid="client-invoices-empty">{pt ? 'Ainda não foi emitida nenhuma fatura a este cliente.' : 'No invoices issued to this client yet.'}</p>
      ) : (
        <div className="space-y-3">
          {[[pt ? 'Em falta' : 'Outstanding', due, true], [pt ? 'Pagas' : 'Paid', paid, false]].map(([label, list, isDue]) => list.length > 0 && (
            <div key={label}>
              <p className={`text-[11px] font-semibold uppercase mb-1 ${isDue ? 'text-red-600' : 'text-green-700'}`}>{label} ({list.length})</p>
              <div className="space-y-1.5">
                {list.map(inv => (
                  <div key={inv.id} className={`border rounded-xl p-2.5 flex flex-wrap items-center justify-between gap-2 ${isDue ? (inv.overdue ? 'border-red-300 bg-red-50/40' : 'border-amber-200') : 'border-green-200'}`} data-testid={`client-invoice-${inv.number}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-xs">{inv.number}</span>
                        {isDue ? (inv.overdue
                          ? <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 font-semibold"><AlertTriangle size={10} /> {pt ? `EM ATRASO · ${inv.days_old}d` : `OVERDUE · ${inv.days_old}d`}</span>
                          : <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold">{pt ? 'EM DÍVIDA' : 'DUE'}</span>)
                          : <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 font-semibold"><CheckCircle2 size={10} /> {pt ? 'PAGA' : 'PAID'}</span>}
                      </p>
                      <p className="text-[11px] text-gray-500">{fmtDT(inv.created_at, pt)} · {inv.items.length} {pt ? 'serviço(s)' : 'job(s)'} · {pt ? 'Total' : 'Total'} {eur(inv.live_total)}{inv.live_paid > 0 && inv.live_due > 0 ? ` · ${pt ? 'pago' : 'paid'} ${eur(inv.live_paid)}` : ''}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {isDue && <span className="text-sm font-bold text-red-600" data-testid="client-invoice-due">{eur(inv.live_due)}</span>}
                      <button onClick={() => pdf(inv)} className="p-1.5 rounded-lg border hover:bg-gray-50" title="PDF" data-testid="client-invoice-pdf"><FileText size={14} /></button>
                      <button onClick={async () => setEmailInv(await full(inv))} className="p-1.5 rounded-lg border hover:bg-gray-50" title="Email" data-testid="client-invoice-email"><Mail size={14} /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      {emailInv && <InvoiceEmailModal inv={emailInv} token={token} language={language} onClose={() => setEmailInv(null)} onSent={(r) => { setToast(pt ? `Enviada para ${r.to}` : `Sent to ${r.to}`); setTimeout(() => setToast(''), 4000); load().catch(() => {}); }} />}
    </div>
  );
};

export default ClientInvoicesList;
